import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Grid,
    Card,
    Chip,
    TablePagination
} from '@mui/material';

import {
    Restaurant as RestaurantIcon,
    People as PeopleIcon,
    Person as PersonIcon,
    School as SchoolIcon,
    Search as SearchIcon,
    Download as DownloadIcon
} from '@mui/icons-material';

import {
    collection,
    getDocs,
    query,
    where,
    orderBy
} from 'firebase/firestore';

import { db } from '../../firebase';
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488";

function Canteenreport() {

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);

    const [fromDate, setFromDate] = useState(
        new Date().toISOString().split('T')[0]
    );

    const [toDate, setToDate] = useState(
        new Date().toISOString().split('T')[0]
    );

    const [mealType, setMealType] = useState("All");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // SUMMARY
    const [totalStudents, setTotalStudents] = useState(0);
    const [totalStaff, setTotalStaff] = useState(0);
    const [totalGuests, setTotalGuests] = useState(0);
    const [grandTotal, setGrandTotal] = useState(0);

    const mealTypes = [
        "All",
        "Breakfast",
        "Lunch",
        "Snacks",
        "Dinner"
    ];

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = async () => {

        if (!fromDate || !toDate) {
            return Swal.fire("Error", "Please select dates", "error");
        }

        setLoading(true);

        try {

            const q = query(
                collection(db, "canteen_attendance"),
                orderBy("date", "desc")
            );

            const snap = await getDocs(q);

            let data = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // DATE FILTER
            data = data.filter(item => {
                return item.date >= fromDate && item.date <= toDate;
            });

            // MEAL FILTER
            if (mealType !== "All") {
                data = data.filter(item => item.mealType === mealType);
            }

            setReportData(data);

            // SUMMARY
            let students = 0;
            let staff = 0;
            let guests = 0;
            let total = 0;

            data.forEach(item => {
                students += Number(item.studentCount || 0);
                staff += Number(item.staffCount || 0);
                guests += Number(item.guestCount || 0);
                total += Number(item.totalHeadCount || 0);
            });

            setTotalStudents(students);
            setTotalStaff(staff);
            setTotalGuests(guests);
            setGrandTotal(total);

        } catch (err) {
            console.log(err);
            Swal.fire("Error", err.message, "error");
        }

        setLoading(false);
    };

    const exportCSV = () => {

        if (reportData.length === 0) {
            return Swal.fire("No Data", "No report data found", "warning");
        }

        const headers = [
            "Sr No",
            "Date",
            "Meal",
            "Timing",
            "Students",
            "Staff",
            "Guests",
            "Total Plates"
        ];

        const rows = reportData.map((row, index) => [
            index + 1,
            row.date,
            row.mealType,
            row.timingTitle || "",
            row.studentCount || 0,
            row.staffCount || 0,
            row.guestCount || 0,
            row.totalHeadCount || 0
        ]);

        let csvContent =
            headers.join(",") + "\n" +
            rows.map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `canteen_report_${fromDate}_to_${toDate}.csv`;
        link.click();
    };

    return (
        <Box p={1} sx={{ background: "#f8fafc", minHeight: "100vh" }}>

            {/* HEADER */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }} elevation={0}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                >

                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            Canteen Report
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Date wise meal & plate consumption report
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={2}>

                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            sx={{ bgcolor: PRIMARY }}
                            onClick={fetchReport}
                        >
                            Get Report
                        </Button>

                        {/* <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportCSV}
                        >
                            Export CSV
                        </Button> */}

                    </Stack>

                </Stack>
            </Paper>

            {/* FILTERS */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }} elevation={0}>

                <Grid container spacing={2}>

                    <Grid item xs={12} md={3}>
                        <TextField
                            label="From Date"
                            type="date"
                            fullWidth
                            size="small"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            label="To Date"
                            type="date"
                            fullWidth
                            size="small"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            label="Meal Type"
                            fullWidth
                            size="small"
                            value={mealType}
                            onChange={(e) => setMealType(e.target.value)}
                        >
                            {mealTypes.map(item => (
                                <option key={item} value={item}>
                                    {item}
                                </option>
                            ))}
                        </TextField>
                    </Grid>

                </Grid>

            </Paper>

            {/* SUMMARY CARDS */}
            <Grid container spacing={2} mb={3}>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{
                        p: 2,
                        textAlign: "center",
                        bgcolor: PRIMARY,
                        color: "#fff"
                    }}>
                        <RestaurantIcon />
                        <Typography variant="h4" fontWeight="bold">
                            {grandTotal}
                        </Typography>
                        <Typography variant="body2">
                            Total Plates
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: "center" }}>
                        <SchoolIcon color="primary" />
                        <Typography variant="h4" fontWeight="bold">
                            {totalStudents}
                        </Typography>
                        <Typography variant="body2">
                            Students
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: "center" }}>
                        <PeopleIcon color="secondary" />
                        <Typography variant="h4" fontWeight="bold">
                            {totalStaff}
                        </Typography>
                        <Typography variant="body2">
                            Staff
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 2, textAlign: "center" }}>
                        <PersonIcon color="warning" />
                        <Typography variant="h4" fontWeight="bold">
                            {totalGuests}
                        </Typography>
                        <Typography variant="body2">
                            Guests
                        </Typography>
                    </Card>
                </Grid>

            </Grid>

            {/* TABLE */}
            <Paper sx={{ borderRadius: 3 }} elevation={0}>

                <TableContainer>

                    <Table size="small">

                        <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                            <TableRow>
                                <TableCell><b>Sr No</b></TableCell>
                                <TableCell><b>Date</b></TableCell>
                                <TableCell><b>Meal</b></TableCell>
                                <TableCell><b>Timing</b></TableCell>
                                <TableCell align="center"><b>Students</b></TableCell>
                                <TableCell align="center"><b>Staff</b></TableCell>
                                <TableCell align="center"><b>Guests</b></TableCell>
                                <TableCell align="center"><b>Total Plates</b></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {loading ? (

                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress sx={{ my: 4 }} />
                                    </TableCell>
                                </TableRow>

                            ) : reportData.length === 0 ? (

                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No Records Found
                                    </TableCell>
                                </TableRow>

                            ) : (

                                reportData
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map((row, index) => (

                                        <TableRow key={row.id} hover>

                                            <TableCell>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            <TableCell>
                                                {row.date}
                                            </TableCell>

                                            <TableCell>
                                                <Chip
                                                    label={row.mealType}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>

                                            <TableCell>
                                                {row.timingTitle || "-"}
                                            </TableCell>

                                            <TableCell align="center">
                                                {row.studentCount || 0}
                                            </TableCell>

                                            <TableCell align="center">
                                                {row.staffCount || 0}
                                            </TableCell>

                                            <TableCell align="center">
                                                {row.guestCount || 0}
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography
                                                    fontWeight="bold"
                                                    color="success.main"
                                                >
                                                    {row.totalHeadCount || 0}
                                                </Typography>
                                            </TableCell>

                                        </TableRow>

                                    ))

                            )}

                        </TableBody>

                    </Table>

                </TableContainer>

                <TablePagination
                    component="div"
                    count={reportData.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />

            </Paper>

        </Box>
    );
}

export default Canteenreport;